import { createContext, useContext, useState } from 'react'

import { useParams } from 'react-router-dom'

import { ConfirmModalContext } from '../../components/ConfirmModal'
import { FeedbackAlertContext } from '../../components/FeedbackAlert'
import {
  IFinishedItemCategory,
  IItemTraceabilityStep,
  IItemWithFinishedItemProfile,
} from '../../sdk/finished-item-profile/finished-item-profile.types'
import { sdk } from '../../services/sdk'

import { EditItemTraceabilityStepForm } from './components/EditItemTraceabilityStepModal/edit-item-traceability-step-modal.types'
import { QRCodeDetailsFeedstockForm } from './components/QRCodeDetailsFeedstockFormModal/qrcode-details-feedstock-form-modal.types'
import { QRCodeDetailsMetricsForm } from './components/QRCodeDetailsMetricsFormModal/qrcode-details-metrics-form-modal.types'
import { QRCodeDetailsPartForm } from './components/QRCodeDetailsPartFormModal/qrcode-details-part-form-modal.types'
import { QRCodeDetailsProductForm } from './components/QRCodeDetailsProductFormModal/qrcode-details-product-form-modal.types'

interface QRCodeDetailsDataContext {
  qrcode: IItemWithFinishedItemProfile | undefined
  finishedItemCategories: IFinishedItemCategory[]
  itemTraceabilitySteps: IItemTraceabilityStep[]
  actions: {
    fetchQrcode: () => Promise<IItemWithFinishedItemProfile | undefined>
    getFinishedItemCategories: () => Promise<void>
    saveFinishedItemProfile: (
      body: QRCodeDetailsProductForm,
      images?: File[],
      deletedImages?: string[],
    ) => Promise<void>
    saveFinishedItemProfilePart: (body: QRCodeDetailsPartForm) => Promise<void>
    saveFeedstockItemProfile: (
      partId: string,
      body: QRCodeDetailsFeedstockForm,
    ) => Promise<void>
    deleteFinishedItemProfilePart: (id: string) => Promise<void>
    deleteFeedstockItemProfile: (id: string) => Promise<void>
    fetchItemTraceabilitySteps: () => Promise<void>
    saveItemTraceabilityStep: (
      title: string,
      body: EditItemTraceabilityStepForm,
    ) => Promise<void>
    createItemTraceabilityStepImage: (
      title: string,
      image: File,
    ) => Promise<string | undefined>
    saveFinishedItemProfileMetrics: (
      body: QRCodeDetailsMetricsForm,
    ) => Promise<void>
  }
}

interface QRCodeDetailsProviderProps {
  children: React.ReactNode
}

export const QRCodeDetailsContext = createContext(
  {} as QRCodeDetailsDataContext,
)

export function QRCodeDetailsProvider(props: QRCodeDetailsProviderProps) {
  const [qrcode, setQrcode] = useState<IItemWithFinishedItemProfile>()
  const [finishedItemCategories, setFinishedItemCategories] = useState<
    IFinishedItemCategory[]
  >([])
  const [itemTraceabilitySteps, setItemTraceabilitySteps] = useState<
    IItemTraceabilityStep[]
  >([])

  const { code } = useParams()
  const { confirmModal } = useContext(ConfirmModalContext)
  const { feedbackAlert } = useContext(FeedbackAlertContext)

  const fetchQrcode = async () => {
    if (!code) return

    const qrcode = await sdk.finishedItemProfile.getFinishedItemProfile(code)
    setQrcode(qrcode)
    return qrcode
  }

  const getFinishedItemCategories = async () => {
    try {
      const response = await sdk.finishedItemProfile.getCategories()

      setFinishedItemCategories(response)
    } catch (err) {}
  }

  const saveFinishedItemProfile = async (
    body: QRCodeDetailsProductForm,
    images?: File[],
    deletedImages?: string[],
  ) => {
    await sdk.finishedItemProfile.saveFinishedItemProfile(body.code, body)

    if (deletedImages?.length) {
      for (const image of deletedImages) {
        await sdk.finishedItemProfile.deleteFinishedItemProfileImage(
          body.code,
          image,
        )
      }
    }

    if (images?.length) {
      for (const image of images) {
        const data = new FormData()
        data.append('image', image)

        await sdk.finishedItemProfile.uploadFinishedItemProfileImage(
          body.code,
          data,
        )
      }
    }

    fetchQrcode()
  }

  const saveFinishedItemProfilePart = async (body: QRCodeDetailsPartForm) => {
    if (!code) return

    await sdk.finishedItemProfile.saveFinishedItemProfilePart(code, body)

    fetchQrcode()
  }

  const deleteFinishedItemProfilePart = async (id: string) => {
    if (
      await confirmModal(
        'Deseja excluir a parte?',
        'Ao excluir não será possível recuperar os dados da parte do item',
      )
    ) {
      try {
        await sdk.finishedItemProfile.deleteFinishedItemProfilePart(id)
        fetchQrcode()
      } catch (err) {
        feedbackAlert((err as any)?.error?.message, 'error')
      }
    }
  }

  const saveFeedstockItemProfile = async (
    partId: string,
    body: QRCodeDetailsFeedstockForm,
  ) => {
    await sdk.finishedItemProfile.saveFeedstockItemProfile(partId, body)

    fetchQrcode()
  }

  const deleteFeedstockItemProfile = async (id: string) => {
    if (
      await confirmModal(
        'Deseja excluir a matéria-prima?',
        'Ao excluir não será possível recuperar os dados da matéria-prima do item',
      )
    ) {
      await sdk.finishedItemProfile.deleteFeedstockItemProfile(id)
      fetchQrcode()
    }
  }

  const fetchItemTraceabilitySteps = async () => {
    if (!code) return

    const response =
      await sdk.finishedItemProfile.getItemTraceabilitySteps(code)

    setItemTraceabilitySteps([...response.steps])
  }

  const saveItemTraceabilityStep = async (
    title: string,
    body: EditItemTraceabilityStepForm,
  ) => {
    await sdk.finishedItemProfile.saveItemTraceabilityStep({
      title,
      itemCode: code ?? '',
      name: body.name,
      description: body.description,
      shortDescription: body.shortDescription,
      assets: {
        images: body.images,
        videos: body.videos,
      },
    })

    await fetchItemTraceabilitySteps()
  }

  const createItemTraceabilityStepImage = async (
    title: string,
    image: File,
  ): Promise<string | undefined> => {
    if (!code) return

    return await sdk.finishedItemProfile.createItemTraceabilityStepImage(
      title,
      code,
      image,
    )
  }

  const saveFinishedItemProfileMetrics = async (
    body: QRCodeDetailsMetricsForm,
  ) => {
    if (!code) return

    await sdk.finishedItemProfile.updateFinishedItemProfileMetrics(
      code,
      body.metrics,
    )

    await fetchItemTraceabilitySteps()

    fetchQrcode()
  }

  return (
    <QRCodeDetailsContext.Provider
      value={{
        qrcode,
        finishedItemCategories,
        itemTraceabilitySteps,
        actions: {
          fetchQrcode,
          getFinishedItemCategories,
          saveFinishedItemProfile,
          saveFinishedItemProfilePart,
          saveFeedstockItemProfile,
          deleteFinishedItemProfilePart,
          deleteFeedstockItemProfile,
          fetchItemTraceabilitySteps,
          saveItemTraceabilityStep,
          createItemTraceabilityStepImage,
          saveFinishedItemProfileMetrics,
        },
      }}
    >
      {props.children}
    </QRCodeDetailsContext.Provider>
  )
}
